/**
 * UnverifiedUsersModal - Modal d'affichage des utilisateurs non vérifiés
 * 
 * Affiche la liste des utilisateurs non vérifiés avec leurs emails pour identification
 * et relance éventuelle.
 * 
 * Utilisation :
 * ```tsx
 * <UnverifiedUsersModal
 *   id="unverifiedUsersModal"
 *   companyId={companyId}
 *   accessToken={accessToken}
 *   onClose={() => setShowUnverified(false)}
 * />
 * ```
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FamlaServices } from '../../services';
import { Spiner } from '../../components';

interface UnverifiedUser {
    id: string;
    email: string;
    fullname: string;
    created_at: string | null;
    role: string;
}

interface UnverifiedUsersModalProps {
    id: string;
    companyId: string;
    accessToken: string;
    onClose?: () => void;
}

const UnverifiedUsersModal: React.FC<UnverifiedUsersModalProps> = ({
    id,
    companyId,
    accessToken,
    onClose
}) => {
    const { t } = useTranslation();
    const [users, setUsers] = useState<UnverifiedUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    const closeRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        loadUnverifiedUsers();
    }, [companyId, accessToken]);

    const loadUnverifiedUsers = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await FamlaServices(
                "api_host",
                `/users/company/${companyId}/unverified`,
                "GET",
                "",
                accessToken
            );

            if (response.status === 200 && response.body?.data) {
                setUsers(response.body.data.users || []);
            } else {
                setError('Failed to load unverified users');
            }
        } catch (err) {
            console.error('[UnverifiedUsersModal] Error loading users:', err);
            setError('An error occurred while loading unverified users');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        closeRef.current?.click();
        if (onClose) {
            setTimeout(onClose, 300);
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString();
        } catch {
            return 'N/A';
        }
    };

    const copyEmail = (email: string) => {
        navigator.clipboard.writeText(email);
    };

    const copyAllEmails = () => {
        const emails = users.map(u => u.email).join(', ');
        navigator.clipboard.writeText(emails);
    };

    const modalContent = (
        <div className="modal fade" id={id} tabIndex={-1} aria-labelledby={`${id}Label`} aria-hidden="true" ref={modalRef}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content bg-dark text-light">
                    <div className="modal-header border-secondary">
                        <h5 className="modal-title" id={`${id}Label`}>
                            <i className="fa-solid fa-user-clock me-2"></i>
                            {t('dashboard.unverifiedUsers')} ({users.length})
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            ref={closeRef}
                        ></button>
                    </div>
                    <div className="modal-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spiner className="text-warning spinner-border-sm" classParent="d-flex justify-content-center align-items-center" />
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger" role="alert">
                                <i className="fa-solid fa-exclamation-triangle me-2"></i>
                                {error}
                            </div>
                        ) : users.length === 0 ? (
                            <div className="alert alert-info" role="alert">
                                <i className="fa-solid fa-info-circle me-2"></i>
                                No unverified users found.
                            </div>
                        ) : (
                            <>
                                <div className="mb-3 d-flex justify-content-between align-items-center">
                                    <p className="mb-0 text-muted">
                                        <i className="fa-solid fa-info-circle me-2"></i>
                                        Liste des utilisateurs non vérifiés pour identification et relance
                                    </p>
                                    <button 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={copyAllEmails}
                                        title="Copy all emails"
                                    >
                                        <i className="fa-solid fa-copy me-1"></i>
                                        Copy All Emails
                                    </button>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-dark table-hover">
                                        <thead>
                                            <tr>
                                                <th scope="col">Email</th>
                                                <th scope="col">Name</th>
                                                <th scope="col">Role</th>
                                                <th scope="col">Created</th>
                                                <th scope="col">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.map((user) => (
                                                <tr key={user.id}>
                                                    <td>
                                                        <strong>{user.email}</strong>
                                                    </td>
                                                    <td>{user.fullname || 'N/A'}</td>
                                                    <td>
                                                        <span className="badge bg-secondary">
                                                            {user.role}
                                                        </span>
                                                    </td>
                                                    <td className="text-muted">
                                                        {formatDate(user.created_at)}
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-outline-light"
                                                            onClick={() => copyEmail(user.email)}
                                                            title="Copy email"
                                                        >
                                                            <i className="fa-solid fa-copy"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="modal-footer border-secondary">
                        <button type="button" className="btn btn-secondary" onClick={handleClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default UnverifiedUsersModal;
